import { useMemo } from "react";
import { InternalApplicationStatuses } from "../../MPP-Common-SPA/common/constants/statuses";
import { useGetApplicationListQuery } from '../../Store/api/application/applicationApi';

const checkApplicationCardinality = ({ applicationList, applicationType }) => {
  if (applicationList) {
    const existingApplications = applicationList.filter(
      (item) => item.applicationType === applicationType
    );

    if ((existingApplications ?? []).length > 0) {
      const inProgress = existingApplications.find(
        x => x.internalStatus === InternalApplicationStatuses.Draft || 
             x.internalStatus === InternalApplicationStatuses.Submitted || 
             x.internalStatus === InternalApplicationStatuses.FollowUp || 
             x.internalStatus === InternalApplicationStatuses.Withdrawn
      );
      if (inProgress) {
        return {
          applicationAlreadySubmitted: false,
          applicationInProgress: true,
          inProgressApplicationCode: inProgress.uniqueApplicationCode,
        };
      }

      return {
        applicationAlreadySubmitted: true,
        applicationInProgress: false,
        inProgressApplicationCode: null,
      };
    }
  }

  return {
    applicationAlreadySubmitted: false,
    applicationInProgress: false,
  };
};


const checkApplicationCopy = ({ list }) => {
  if (list) {
    const existingApplications = list.filter(
      (item) => item.applicationStatus === InternalApplicationStatuses.Submitted
    );

    if ((existingApplications ?? []).length > 0) {
      const { id, applicationDataJson } = existingApplications[0] ?? {};

      const applicationData = JSON.parse(applicationDataJson ?? "{}");

      return {
        allowCopyFromPreviousApplication: true,
        previousApplication: { id, applicationData },
      };
    }
  }

  return {
    allowCopyFromPreviousApplication: false,
    previousApplication: null,
  };
};

export const useApplicationCardinality = ({
  userApplicationData,
  applicationType,
  isLoggedIn,
  template,
  currentStep,
}) => {

  const { data: applicationList, isLoading, error } = useGetApplicationListQuery(undefined, {
    skip: !isLoggedIn,
  });

  const {
    applicationAlreadySubmitted,
    applicationInProgress,
    inProgressApplicationCode,
    allowCopyFromPreviousApplication = false,
    previousApplication = null,
  } = useMemo(() => {
    if (!isLoggedIn || isLoading || error || !applicationList) {
      return {
        applicationAlreadySubmitted: false,
        applicationInProgress: false,
      };
    }

    if (!userApplicationData || !applicationType || !template) {
      return {
        applicationAlreadySubmitted: false,
        applicationInProgress: false,
      };
    }

    const { applicationAlreadySubmitted, applicationInProgress, inProgressApplicationCode } =
      checkApplicationCardinality({ applicationList, applicationType });

    if (!applicationInProgress && !applicationAlreadySubmitted) {
      const { allowCopyFromPreviousApplication, previousApplication } =
        checkApplicationCopy({ userApplicationData, applicationList, applicationType });

      return {
        applicationAlreadySubmitted,
        applicationInProgress,
        allowCopyFromPreviousApplication,
        previousApplication,
        inProgressApplicationCode,
      };
    }

    return { applicationAlreadySubmitted, applicationInProgress, inProgressApplicationCode };
  }, [userApplicationData, isLoggedIn, template, applicationList, isLoading, error, applicationType]);

  return {
    applicationAlreadySubmitted,
    applicationInProgress,
    inProgressApplicationCode,
    allowCopyFromPreviousApplication,
    previousApplication,
  };
};
